/*
 *
 * ListPointOfSales
 *
 * @version 1.0.0
 * @author Giorgia Cuozzo
 * @since 14-May-2025
 */

import React from 'react'
import PropTypes from 'prop-types'
import Dialog from '@mui/material/Dialog'
import { TableFooter } from '@mui/material'

import { Dismiss } from '@nexidigital/iconlibrary'
import MerchantTable from '@nexidigital/merchanttable'
import MerchantTableBadge from '@nexidigital/merchanttablebadge'
import MerchantPagination from '@nexidigital/merchantpagination'
import { nullSafe } from 'Utility/globalMethods'
import Toggle from 'Hoc/ToggleComponent'
import { useMobileDetect } from 'Hoc/ListenerProvider'
import FlowTemplate from 'Components/Template/FlowTemplate'
import languageModule from './languageModule'

import './ListPointOfSales.scss'

const ListPointOfSales = ({ open, pointOfSalesData, onClickClose, pagination, onPageChange, showPagination, sectionTitle, totalLabel }) => {
  const { isSmallScreen, isBigScreen } = useMobileDetect()

  return (
    <Toggle>
      <Dialog fullScreen open={open} className='ListPointOfSales'>
        <FlowTemplate
          flowBackground
          titleNavigation={languageModule.titleNavigation}
          iconCloseRight={<Dismiss size={isSmallScreen ? '24' : '20'} type='line' />}
          labelButtonRightNav={languageModule.close}
          onClickClose={onClickClose}
          emptyPage
        >
          <div className='listPointOfSales'>
            <div className='listPointOfSalesHeader'>
              <h1 className='listPointOfSalesTitle'>
                {sectionTitle || languageModule.sectionTitle}
              </h1>
            </div>

            <div className='listPointOfSalesTable'>
              <p className='listPointOfSalesTableTitle'>
                {totalLabel}: <b>{pagination?.total || nullSafe(() => pointOfSalesData.length, 0)}</b>
              </p>
              {isBigScreen
                ? (
                  <>
                    <div className='listPointOfSalesList'>
                      {pointOfSalesData.map((pos, index) => (
                        <div className='listPointOfSalesRow' key={pos.code || pos.shopId || pos.id || pos.name || index}>
                          <div className='listPointOfSalesRowItem'>
                            <b className='listPointOfSalesRowName'>{pos.name}</b>
                            {pos.typology && (
                              <MerchantTableBadge symbol label={pos.typology} status='brand' />
                            )}
                          </div>
                          <div className='listPointOfSalesRowAddress'>
                            {pos.code && (
                              <MerchantTableBadge label={pos.code} status='neutralLight' />
                            )}
                            {pos.address && <p className='listPointOfSalesAddress'>{pos.address}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                    {showPagination && pagination && (
                      <div className='paginationContainer'>
                        <MerchantPagination {...pagination} onChange={onPageChange} />
                      </div>
                    )}
                  </>
                  )
                : (
                  <>
                    <MerchantTable
                      columns={[
                        { id: 'pv', label: languageModule.columnPV, sx: { width: '30%' } },
                        ...(pointOfSalesData.some(pos => pos.typology)
                          ? [{ id: 'typology', label: languageModule.columnTypology, sx: { width: '20%' } }]
                          : []),
                        { id: 'code', label: languageModule.columnCode, sx: { width: '15%' } },
                        ...(pointOfSalesData.some(pos => pos.address)
                          ? [{ id: 'address', label: languageModule.columnAddress, sx: { width: '40%' } }]
                          : [])
                      ]}
                      rows={pointOfSalesData.map(pos => ({
                        pv: <b>{pos.name}</b>,
                        ...(pos.typology && { typology: <MerchantTableBadge symbol label={pos.typology} status='brand' /> }),
                        code: <MerchantTableBadge label={pos.code} status='neutralLight' />,
                        ...(pos.address && { address: pos.address })
                      }))}
                      sx={{ border: 0 }}
                    />
                    {showPagination && pagination && (
                      <TableFooter className='paginationContainer'>
                        <MerchantPagination {...pagination} onChange={onPageChange} />
                      </TableFooter>
                    )}
                  </>
                  )}
            </div>
          </div>
        </FlowTemplate>
      </Dialog>
    </Toggle>
  )
}

ListPointOfSales.propTypes = {
  // Add here some propTypes
  // and set a minimal description i.e.
  /** Description of prop "baz". */
  open: PropTypes.bool,
  onClickClose: PropTypes.func,
  pointOfSalesData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      typology: PropTypes.string,
      code: PropTypes.string,
      address: PropTypes.string
    })
  ),
  pagination: PropTypes.shape({
    total: PropTypes.number,
    page: PropTypes.number,
    pageSize: PropTypes.number,
    pageCount: PropTypes.number
  }),
  onPageChange: PropTypes.func,
  showPagination: PropTypes.bool,
  sectionTitle: PropTypes.object,
  totalLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
}

ListPointOfSales.defaultProps = {
  // Add here some default propTypes values
  open: false,
  onClickClose: () => { },
  pointOfSalesData: [],
  pagination: null,
  onPageChange: () => { },
  showPagination: false,
  sectionTitle: null,
  totalLabel: languageModule.total
}

export default ListPointOfSales
